import React, { useCallback, useEffect, useState } from 'react';
import { graphql } from 'gatsby';
import { Layout } from '@components/Layout';
import { Seo, PreloadHeroImage } from '@components';
import { createComponent } from '@helpers/createComponent';
import { Box, Flex, Heading } from '@chakra-ui/react';
import Section from '@components/atoms/Section';
import Wrapper from '@components/atoms/Wrapper';
import Button from '@components/atoms/Button';
import { ArrowRightIcon } from '@components/atoms/icons';
import FormFieldSelect from '@components/molecules/FormFieldSelect';
import FormFieldText from '@components/molecules/FormFieldText';
import FormFieldTextarea from '@components/molecules/FormFieldTextarea';
import FormFieldNumber from '@components/molecules/FormFieldNumber';
import FormFieldEmail from '@components/molecules/FormFieldEmail';
import FormFieldDate from '@components/molecules/FormFieldDate';
import FormFieldFile from '@components/molecules/FormFieldFile';
import FormFieldCheckbox from '@components/molecules/FormFieldCheckbox';
import FormStatus from '@components/organisms/FormStatus';
import Breadcrumbs from '@components/atoms/Breadcrumbs';
import LexicalRichText from '@components/atoms/LexicalRichText';
import dataLayerPush from '@helpers/dataLayerPush';
import { postForm, mapFieldGroups } from '../helpers/formHelpers';

// #region Form Components
const FieldComponents = {
  text: FormFieldText,
  textarea: FormFieldTextarea,
  selection: FormFieldSelect,
  number: FormFieldNumber,
  email: FormFieldEmail,
  date: FormFieldDate,
  file: FormFieldFile,
  checkbox: FormFieldCheckbox
};
// #endregion

// #region Page Component
const ContactPage = ({ data, pageContext }) => {
  const { breadcrumbs } = pageContext;
  const { strapiContactForm } = data;
  const {
    formId,
    heading,
    tagline,
    content,
    defaultPlaceholder,
    defaultErrorMessage,
    submitButtonText,
    responseText,
    sendingMessageText,
    sendAgainText,
    fieldGroups
  } = strapiContactForm;

  const parsedFieldGroups = mapFieldGroups(fieldGroups);

  const [formValid, setFormValid] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formDidError, setFormDidError] = useState(false);
  const [invalidFields, setInvalidFields] = useState([]);

  const handleOnFieldRendered = ({ fieldId, required }) => {
    if (required === true) {
      setInvalidFields((prev) => [...prev, fieldId]);
    }
  };

  const handleOnFieldChange = ({ fieldId, isValid }) => {
    const filtered = invalidFields.filter(
      (invalidFieldId) => invalidFieldId !== fieldId
    );

    if (isValid === false) {
      setInvalidFields([...filtered, fieldId]);
    } else {
      setInvalidFields(filtered);
    }
  };

  const handleOnFormSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      const mappedValues = [];

      for (let i = 0; i < event.target.elements.length; i += 1) {
        const currentElement = event.target.elements[i];

        const { id, name, value, selectedIndex } = currentElement;

        if (id.length > 0) {
          mappedValues.push({
            id,
            name,
            value,
            selectedIndex,
            dataId: currentElement.getAttribute(`dataid`)
          });
        }
      }

      setSubmitAttempted(true);
      setFormDidError(false);

      if (formValid === true) {
        setSubmitAttempted(false);
        setSubmitInProgress(true);

        const { success, data: returnedFormData } = await postForm(
          `/api/post-form/`,
          {
            formId,
            values: mappedValues
          }
        );

        setSubmitInProgress(false);

        if (success === false) {
          // eslint-disable-next-line no-console
          console.log(`Returned error data:`, returnedFormData);

          setFormDidError(true);
          return;
        }

        setSubmitSuccess(true);
      } else if (typeof window !== `undefined` && invalidFields.length > 0) {
        const [firstInvalidField] = invalidFields;
        const resolvedField = document.getElementById(firstInvalidField);

        if (resolvedField) {
          resolvedField.scrollIntoView({
            behavior: `smooth`,
            block: `center`,
            inline: `nearest`
          });
        }
      }
    },
    [formValid, formId, invalidFields]
  );

  const handleOnSendAnother = useCallback(() => {
    setFormDidError(false);
    setSubmitInProgress(false);
    setSubmitSuccess(false);
    setSubmitAttempted(false);
    setFormValid(false);
  }, []);

  useEffect(() => {
    setFormValid(invalidFields.length === 0);
  }, [invalidFields]);

  return (
    <Layout>
      <Flex width="100%" flexDirection={{ base: `column`, md: `row` }} pb="24">
        <Section
          h="fit-content"
          w={{ base: `100%`, md: `50%` }}
          pt={{ base: `32`, md: `44` }}
          pb={{ base: `2`, md: `12` }}
          mb="0"
          bg="primary.dark-blue"
          borderBottomRightRadius={{ base: `none`, md: `lg` }}>
          <Flex
            px={{ base: 5, md: 14 }}
            maxWidth="45rem"
            h="100%"
            ml="auto"
            flexDirection="column">
            {breadcrumbs.crumbs && (
              <Breadcrumbs
                id="contact-form-breadcrumbs"
                color="white"
                background="transparent"
                wrapperProps={{
                  px: 0,
                  pb: 4,
                  sx: {
                    a: { color: `white` },
                    div: { color: `white` }
                  }
                }}
                crumbs={breadcrumbs.crumbs}
              />
            )}
            {tagline !== null && (
              <Heading
                as="p"
                pb={{ base: `2`, md: `26` }}
                variant="detail"
                size="xs"
                color="secondary.light-grey">
                {tagline}
              </Heading>
            )}
            {heading !== null && (
              <Heading
                as="h2"
                size="4xl"
                mt="auto"
                mb="4"
                color="secondary.light-grey">
                {heading}
              </Heading>
            )}
            {content?.data?.content !== null && (
              <LexicalRichText
                props={{ px: `0`, color: `white` }}
                data={{
                  richText: {
                    data: {
                      richText: content.data.content
                    }
                  }
                }}
              />
            )}
          </Flex>
        </Section>
        <Section
          w={{ base: `100%`, md: `50%` }}
          px={{ base: `5`, md: `28` }}
          pt={{ base: `32`, md: `44` }}
          p="0"
          mb="0"
          bg={{ base: `primary.dark-blue`, md: `transparent` }}>
          <Box
            pt={{ base: 12, md: 0 }}
            px="5"
            bg="white"
            borderTopLeftRadius="sm"
            borderTopRightRadius="sm">
            {submitInProgress === true ||
            submitSuccess === true ||
            formDidError === true ? (
              <FormStatus
                responseText={responseText}
                sendingMessageText={sendingMessageText}
                sendAgainText={sendAgainText}
                onClick={handleOnSendAnother}
                formDidError={formDidError}
                submitInProgress={submitInProgress}
                submitSuccess={submitSuccess}
              />
            ) : (
              <Flex
                maxWidth={{ base: `none`, md: `25rem` }}
                mr="auto"
                flexDirection="column">
                <Box
                  id="contact-form"
                  as="form"
                  onSubmit={handleOnFormSubmit}
                  noValidate>
                  {parsedFieldGroups?.length > 0 &&
                    parsedFieldGroups.map(({ id, label, richText, fields }) => {
                      return (
                        <Box
                          w="100%"
                          key={`contact-form-field-group-${label}-${id}}`}
                          mb={{ base: `0`, md: `6` }}>
                          <Heading
                            as="p"
                            size="md"
                            color="primary.dark-blue"
                            mb="1.5">
                            {label}
                          </Heading>
                          {richText && (
                            <LexicalRichText
                              data={{ richText }}
                              props={{
                                maxWidth: null,
                                px: null,
                                mx: null,
                                mb: 4,
                                sx: {
                                  '> *:last-child': {
                                    mb: `0`
                                  }
                                }
                              }}
                            />
                          )}
                          {fields?.length > 0 &&
                            fields.map(
                              ({
                                id: fieldId,
                                label: fieldLabel,
                                fieldType,
                                errorMessage,
                                required,
                                placeholder,
                                dataId
                              }) => {
                                if (fieldType === null) {
                                  return (
                                    <div
                                      key={`dynamic-zone-component-${fieldId}`}>
                                      Field type missing for {fieldId}.
                                    </div>
                                  );
                                }

                                const FieldComponent = fieldType.type
                                  ? FieldComponents[String(fieldType.type)]
                                  : null;

                                if (
                                  typeof FieldComponent === `undefined` ||
                                  FieldComponent === null
                                ) {
                                  return (
                                    <div
                                      key={`dynamic-zone-component-${fieldType.type}-${fieldId}`}>
                                      Component <b>{fieldType.type}</b> not
                                      found on {fieldId}.
                                    </div>
                                  );
                                }

                                return (
                                  <FieldComponent
                                    key={`contact-form-field-component-${fieldType.type}-${fieldId}`}
                                    fieldId={fieldId}
                                    dataId={dataId}
                                    submitAttempted={submitAttempted}
                                    defaultPlaceholder={defaultPlaceholder}
                                    overridePlaceholder={placeholder}
                                    required={required}
                                    errorMessage={
                                      errorMessage || defaultErrorMessage
                                    }
                                    fieldLabel={fieldLabel}
                                    fieldValue={fieldType.fieldValue}
                                    fieldList={fieldType.fieldValue}
                                    onReady={handleOnFieldRendered}
                                    onChange={handleOnFieldChange}
                                  />
                                );
                              }
                            )}
                        </Box>
                      );
                    })}
                  <Button
                    type="submit"
                    disabled={formValid === false || submitInProgress === true}
                    rightIcon={<ArrowRightIcon />}>
                    {submitButtonText}
                  </Button>
                </Box>
              </Flex>
            )}
          </Box>
        </Section>
      </Flex>
      <Wrapper>
        {strapiContactForm?.body &&
          strapiContactForm.body.map((props, index) => {
            return createComponent({ ...props, index });
          })}
      </Wrapper>
    </Layout>
  );
};
// #endregion

// #region Page Head
export const Head = ({ location, data }) => {
  const { pathname } = location;
  const { strapiContactForm } = data;

  dataLayerPush({
    event: `page_load`,
    interaction: {
      page_title: strapiContactForm.seo?.metaTitle || `Contact Us`,
      page_slug: pathname
    }
  });

  return (
    <>
      <Seo
        seo={strapiContactForm?.seo}
        pathname={pathname}
        defaultTitle={strapiContactForm?.title}
      />
      {strapiContactForm?.body && (
        <PreloadHeroImage firstComponent={strapiContactForm.body[0]} />
      )}
    </>
  );
};
// #endregion

// #region Query
export const query = graphql`
  query {
    strapiContactForm {
      formId: strapi_id
      tagline
      heading
      content {
        data {
          content
        }
      }
      submitButtonText
      defaultPlaceholder
      defaultErrorMessage
      responseText
      sendingMessageText
      sendAgainText
      submitButtonText
      fieldGroups {
        id
        label
        richText {
          data {
            richText
          }
        }
        fields: strapi_fields {
          id
          fieldType
          label
          required
          dataId
          errorMessage
          placeholder
        }
      }
      seo {
        ...Seo
      }
      body {
        ... on STRAPI__COMPONENT_SECTIONS_MEDIA_AND_TEXT {
          strapiComponent: strapi_component
          title
          tagline
          text
          images {
            localFile {
              childImageSharp {
                gatsbyImageData(layout: FULL_WIDTH)
              }
              url
            }
            alternativeText
          }
          ctas {
            title
            link
            type
          }
          alignment
          color
        }
      }
    }
  }
`;
// #endregion

export default ContactPage;
